import { User, UserPermissions } from '@centecomm/portal-service';
import { LDClient } from 'launchdarkly-react-client-sdk';
import sift, { Query } from 'sift';
import { UserTypeAgnosticSettings } from 'State/Settings/Types/UserTypeAgnosticSettings';
import { LaunchDarklyFlags } from 'utils/Hooks/LaunchDarkly/launchDarklyFlags';

import { FeatureVisibilityContext } from '../Types/FeatureVisibilityContext';

/**
 * Builds the runtime snapshot that feature `visibilityRule`s are evaluated
 * against. Computed once per render and shared across every feature so each rule
 * evaluation is a cheap in-memory match.
 */
export const buildFeatureVisibilityContext = (
    currentUser: User,
    checkPermission: (permission: UserPermissions) => boolean,
    ldClient?: LDClient,
    settings?: UserTypeAgnosticSettings,
): FeatureVisibilityContext => {
    const flags = Object.values(LaunchDarklyFlags).reduce(
        (acc, flag) => ({ ...acc, [flag]: ldClient?.variation(flag, false) === true }),
        {} as Record<LaunchDarklyFlags, boolean>,
    );

    const permissions = Object.values(UserPermissions).filter((permission) => checkPermission(permission));

    return {
        ...flags,
        userType: currentUser?.userType,
        permissions,
        settings,
    };
};

/**
 * Evaluates an optional feature `visibilityRule` against the context. Features
 * without a rule are always considered to pass, leaving the legacy checks in
 * charge.
 */
export const passesVisibilityRule = (
    rule: Query<FeatureVisibilityContext> | undefined,
    context: FeatureVisibilityContext,
): boolean => (rule ? sift(rule)(context) : true);
